import { g17ecoApi, transformResponse } from '@api/g17ecoApi';
import { Action } from '@constants/action';
import { SurveyUserRoles, UserRoles } from '@constants/user';
import { OnboardingStatus } from '@g17eco/types/onboarding';
import { MassDelegation } from '@g17eco/types/survey';
import { SurveyWorkgroup } from '@g17eco/types/workgroup';
import { UtrvIds } from '@g17eco/types/universalTrackerValue';
import { SafeUser, UserMin } from '@g17eco/types/user';
import { SurveyDelegateRole, UtrvDelegateRole } from '@utils/delegation-users';

export type User = Omit<UserMin, 'roles'> &
  Pick<SafeUser, 'email' | 'isStaff'> & {
    status?: OnboardingStatus;
    initiativeRoles?: UserRoles[];
    surveyRoles?: SurveyDelegateRole[];
    roles?: UtrvDelegateRole[];
    workgroups?: SurveyWorkgroup[];
    // Keep count field for backwards compatibility.
    count?: {
      contributed?: number;
      verified?: number;
    };
    utrvIds?: UtrvIds;
  };

export type DelegatedUsers = { contributors: User[]; verifiers: User[]; admins: User[] };

export enum Tag {
  SurveyUsers = 'survey-users',
}

type MutationUsersParams = {
  surveyId: string;
  role: SurveyUserRoles;
  userId: string;
  onboardingEmail?: string;
};

type MassDelegationParams = MassDelegation & {
  action: Action;
  surveyId: string;
};

const getMutationUrl = ({ surveyId, role, userId, onboardingEmail }: MutationUsersParams) => {
  return onboardingEmail
    ? `surveys/${surveyId}/users/${role}/${onboardingEmail}/onboard`
    : `surveys/${surveyId}/users/${role}/${userId}`;
};

export const surveyUsersApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: Object.values(Tag),
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getSurveyUsers: builder.query<DelegatedUsers, { surveyId: string; utrvIds?: string[] }>({
        transformResponse,
        query: ({ surveyId, utrvIds }) => ({
          url: `surveys/${surveyId}/universal-tracker-values/users`,
          method: 'post',
          data: { utrvIds },
        }),
        providesTags: (_result, _error, args) => {
          return [{ type: Tag.SurveyUsers, id: args.surveyId }];
        },
      }),
      addSurveyUser: builder.mutation<User, MutationUsersParams>({
        invalidatesTags: (_result, _error, { surveyId }) => [{ type: Tag.SurveyUsers, id: surveyId }],
        transformResponse,
        query: ({ surveyId, role, userId, onboardingEmail }) => ({
          url: getMutationUrl({ surveyId, role, userId, onboardingEmail }),
          method: 'patch',
        }),
      }),
      removeSurveyUser: builder.mutation<User, MutationUsersParams>({
        invalidatesTags: (_result, _error, { surveyId }) => [{ type: Tag.SurveyUsers, id: surveyId }],
        transformResponse,
        query: ({ surveyId, role, userId, onboardingEmail }) => ({
          url: getMutationUrl({ surveyId, role, userId, onboardingEmail }),
          method: 'delete',
        }),
      }),
      massDelegateUtrvUsers: builder.mutation<void, MassDelegationParams>({
        invalidatesTags: (_result, _error, { surveyId }) => [{ type: Tag.SurveyUsers, id: surveyId }],
        query: ({ surveyId, roles, userIds, utrvIds, action }) => ({
          url: `surveys/${surveyId}/universal-tracker-values/delegation`,
          method: action === Action.Add ? 'post' : 'delete',
          data: { role: roles, userId: userIds, utrvIds },
        }),
      }),
    }),
  });

export const {
  useGetSurveyUsersQuery,
  useAddSurveyUserMutation,
  useRemoveSurveyUserMutation,
  useMassDelegateUtrvUsersMutation,
} = surveyUsersApi;
